import CustomAvatar from "@/components/CustomAvatar";
import PollCardWrapper from "@/components/Polls/PollCardWrapper";
import { Button } from "@/components/ui/button";
import MainLayout from "@/lib/layout/MainLayout";
import { UserDetails } from "@/lib/server-functions/get-user";
import { useCreateFollow } from "@/lib/tanstack-query/hooks/useCreateFollow";
import { usePolls } from "@/lib/tanstack-query/hooks/usePolls";
import { useUser } from "@/lib/tanstack-query/hooks/useUser";
import { QUERY_KEYS } from "@/lib/tanstack-query/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/better-auth/client";
import LoadingSpinner from "@/components/LoadingSpinner";

export const Route = createFileRoute("/_dashboard/profile/$username")({
  component: RouteComponent,
});

function RouteComponent() {
  const [sort, setSort] = useState<"best" | "new">("new");
  const [nsfw, setNsfw] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { ref, inView } = useInView();
  const { username } = Route.useParams();
  const { data: user } = useUser({ username: username });
  const { mutateAsync: createFollow, isError: isFollowError } =
    useCreateFollow();
  const { data: userSession } = authClient.useSession();
  const {
    data: polls,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = usePolls({ authorUsername: username, sort, nsfw });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, isLoading]);

  const handleToggleFollow = async () => {
    if (!user) return;
    if (userSession?.user.id === user.id) return;

    if (user.id)
      queryClient.setQueryData(
        [QUERY_KEYS.USER, user.username],
        (oldData: UserDetails | undefined | null) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            followersCount: oldData.isFollowedByUser
              ? oldData.followersCount - 1
              : oldData.followersCount + 1,
            isFollowedByUser: !oldData.isFollowedByUser,
          };
        }
      );

    await createFollow({
      userIdToFollow: user.id,
    });
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <MainLayout.Header>
        <div className="flex items-start justify-between space-x-6">
          <div className="relative flex flex-col gap-y-2">
            <div className="flex h-24 w-24 items-center justify-center rounded-full">
              {user && (
                <CustomAvatar
                  width="96px"
                  height="96px"
                  name={user.name!}
                  avatarString={user.avatar!}
                  image={user.image}
                />
              )}
            </div>
            <div>
              <h1 className="text-headline-wu mb-2 text-3xl font-bold">
                {user.name}
              </h1>
              <p className="text-secondary-text-wu text-lg">@{user.username}</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {user.pollsCount}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Posts
                </div>
              </div>
              <Link
                to="/followers/$username"
                params={{ username: user.username }}
              >
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {user.followersCount}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Followers
                </div>
              </Link>
              <Link
                to="/following/$username"
                params={{ username: user.username }}
              >
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {user.followingCount}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Following
                </div>
              </Link>
            </div>
          </div>
          {userSession?.user && userSession?.user.id !== user.id ? (
            <Button
              type="button"
              className={`rounded-xl text-base font-semibold ${
                user.isFollowedByUser
                  ? "bg-background dark:hover:bg-button-hover-background-wu ring-2 dark:text-white dark:ring-white"
                  : ""
              }`}
              onClick={handleToggleFollow}
            >
              Follow
            </Button>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Select
            value={sort}
            onValueChange={(value) => setSort(value as "best" | "new")}
          >
            <SelectTrigger className="dark:bg-button-background-wu bg-button-background-wu hover:bg-button-hover-background-wu dark:hover:bg-button-hover-background-wu cursor-pointer rounded-full border-none">
              <SelectValue placeholder="Select a piece type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="best">Best</SelectItem>
              <SelectItem value="new">New</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => setNsfw(!nsfw)}
            className={cn(
              "rounded-full transition-none",
              nsfw
                ? "bg-primary-wu dark:bg-primary-wu hover:bg-primary-wu dark:hover:bg-primary-wu hover:brightness-90"
                : "dark:bg-button-background-wu bg-button-background-wu hover:bg-button-hover-background-wu dark:hover:bg-button-hover-background-wu"
            )}
            variant={"ghost"}
          >
            NSFW
          </Button>
        </div>
      </MainLayout.Header>
      <MainLayout.Content hasPadding={false}>
        <PollCardWrapper polls={polls?.pages.flatMap((page) => page) ?? []} />

        <div>
          {isLoading ? <LoadingSpinner /> : null}
          {hasNextPage ? <LoadingSpinner ref={ref} /> : null}
        </div>
      </MainLayout.Content>
    </>
  );
}
